/**
 * Supabase Profile Duck
 *
 * Manages extended user profile data stored in Supabase.
 * This complements the Sharetribe user data with additional social features.
 *
 * Data Flow:
 * 1. User authenticates via Sharetribe (handled by auth.duck.js)
 * 2. fetchSupabaseProfile() is called to get extended profile from Supabase
 * 3. If profile doesn't exist, it's created via webhook or on-demand sync
 * 4. Profile updates (handle, bio, avatar) are stored in Supabase only
 */

import { storableError } from '../util/errors';
import * as log from '../util/log';
import { apiBaseUrl } from '../util/api';

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const fetchSupabaseProfileRequestOnce = () =>
  fetch(`${apiBaseUrl()}/api/supabase/profile`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

const syncSupabaseProfileRequestOnce = body =>
  fetch(`${apiBaseUrl()}/api/supabase/profile/sync`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
  });

const buildProfileSyncPayload = currentUser => {
  const attributes = currentUser?.attributes || {};
  const profile = attributes.profile || {};
  const publicData = profile.publicData || attributes.publicData || {};
  const profileImage = currentUser?.profileImage;

  return {
    handle: publicData.handle || null,
    display_name: profile.displayName || null,
    first_name: profile.firstName || null,
    last_name: profile.lastName || null,
    bio: attributes.bio || profile.bio || null,
    avatar_url:
      profileImage?.attributes?.variants?.default?.url ||
      profileImage?.attributes?.variants?.['square-small']?.url ||
      null,
  };
};

// ================ Action types ================ //

export const FETCH_SUPABASE_PROFILE_REQUEST = 'app/supabaseProfile/FETCH_SUPABASE_PROFILE_REQUEST';
export const FETCH_SUPABASE_PROFILE_SUCCESS = 'app/supabaseProfile/FETCH_SUPABASE_PROFILE_SUCCESS';
export const FETCH_SUPABASE_PROFILE_ERROR = 'app/supabaseProfile/FETCH_SUPABASE_PROFILE_ERROR';

export const UPDATE_SUPABASE_PROFILE_REQUEST =
  'app/supabaseProfile/UPDATE_SUPABASE_PROFILE_REQUEST';
export const UPDATE_SUPABASE_PROFILE_SUCCESS =
  'app/supabaseProfile/UPDATE_SUPABASE_PROFILE_SUCCESS';
export const UPDATE_SUPABASE_PROFILE_ERROR = 'app/supabaseProfile/UPDATE_SUPABASE_PROFILE_ERROR';

export const SYNC_PROFILE_REQUEST = 'app/supabaseProfile/SYNC_PROFILE_REQUEST';
export const SYNC_PROFILE_SUCCESS = 'app/supabaseProfile/SYNC_PROFILE_SUCCESS';
export const SYNC_PROFILE_ERROR = 'app/supabaseProfile/SYNC_PROFILE_ERROR';

export const CLEAR_SUPABASE_PROFILE = 'app/supabaseProfile/CLEAR_SUPABASE_PROFILE';

// ================ Reducer ================ //

export const initialState = {
  profile: null,
  profileFetchInProgress: false,
  profileFetchError: null,
  profileUpdateInProgress: false,
  profileUpdateError: null,
  syncInProgress: false,
  syncError: null,
};

export default function supabaseProfileReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_SUPABASE_PROFILE_REQUEST:
      return {
        ...state,
        profileFetchInProgress: true,
        profileFetchError: null,
      };
    case FETCH_SUPABASE_PROFILE_SUCCESS:
      return {
        ...state,
        profile: payload,
        profileFetchInProgress: false,
      };
    case FETCH_SUPABASE_PROFILE_ERROR:
      return {
        ...state,
        profileFetchInProgress: false,
        profileFetchError: payload,
      };

    case UPDATE_SUPABASE_PROFILE_REQUEST:
      return {
        ...state,
        profileUpdateInProgress: true,
        profileUpdateError: null,
      };
    case UPDATE_SUPABASE_PROFILE_SUCCESS:
      return {
        ...state,
        profile: { ...state.profile, ...payload },
        profileUpdateInProgress: false,
      };
    case UPDATE_SUPABASE_PROFILE_ERROR:
      return {
        ...state,
        profileUpdateInProgress: false,
        profileUpdateError: payload,
      };

    case SYNC_PROFILE_REQUEST:
      return {
        ...state,
        syncInProgress: true,
        syncError: null,
      };
    case SYNC_PROFILE_SUCCESS:
      return {
        ...state,
        profile: payload,
        syncInProgress: false,
      };
    case SYNC_PROFILE_ERROR:
      return {
        ...state,
        syncInProgress: false,
        syncError: payload,
      };

    case CLEAR_SUPABASE_PROFILE:
      return initialState;

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getSupabaseProfile = state => state.supabaseProfile?.profile || null;
export const isProfileFetchInProgress = state =>
  state.supabaseProfile?.profileFetchInProgress || false;
export const isProfileUpdateInProgress = state =>
  state.supabaseProfile?.profileUpdateInProgress || false;
export const isProfileSyncInProgress = state => state.supabaseProfile?.syncInProgress || false;
export const getProfileErrors = state => ({
  fetchError: state.supabaseProfile?.profileFetchError,
  updateError: state.supabaseProfile?.profileUpdateError,
  syncError: state.supabaseProfile?.syncError,
});

// Profile completion selectors
export const getProfileCompletionStatus = state => {
  const profile = getSupabaseProfile(state);
  if (!profile) return { isComplete: false, completionPercentage: 0, missingFields: [] };

  const requiredFields = ['handle', 'bio', 'avatar_url'];
  const optionalFields = ['social_links', 'preferences'];
  const allFields = [...requiredFields, ...optionalFields];

  const completedFields = allFields.filter(field => {
    const value = profile[field];
    if (field === 'social_links' || field === 'preferences') {
      return value && Object.keys(value).length > 0;
    }
    return (value && value.trim && value.trim().length > 0) || (value && !value.trim);
  });

  const missingFields = allFields.filter(field => !completedFields.includes(field));
  const completionPercentage = Math.round((completedFields.length / allFields.length) * 100);

  return {
    isComplete: completionPercentage >= 80, // 80% completion threshold
    completionPercentage,
    missingFields,
    completedFields,
  };
};

export const hasExtendedProfile = state => {
  const profile = getSupabaseProfile(state);
  return profile && (profile.handle || profile.bio || profile.avatar_url);
};

// ================ Action creators ================ //

export const fetchSupabaseProfileRequest = () => ({
  type: FETCH_SUPABASE_PROFILE_REQUEST,
});

export const fetchSupabaseProfileSuccess = profile => ({
  type: FETCH_SUPABASE_PROFILE_SUCCESS,
  payload: profile,
});

export const fetchSupabaseProfileError = error => ({
  type: FETCH_SUPABASE_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const updateSupabaseProfileRequest = () => ({
  type: UPDATE_SUPABASE_PROFILE_REQUEST,
});

export const updateSupabaseProfileSuccess = profile => ({
  type: UPDATE_SUPABASE_PROFILE_SUCCESS,
  payload: profile,
});

export const updateSupabaseProfileError = error => ({
  type: UPDATE_SUPABASE_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const syncProfileRequest = () => ({
  type: SYNC_PROFILE_REQUEST,
});

export const syncProfileSuccess = profile => ({
  type: SYNC_PROFILE_SUCCESS,
  payload: profile,
});

export const syncProfileError = error => ({
  type: SYNC_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const clearSupabaseProfile = () => ({
  type: CLEAR_SUPABASE_PROFILE,
});

// ================ Thunks ================ //

/**
 * Fetch Supabase profile for the current user
 * This goes through the backend API to ensure proper authentication
 */
export const fetchSupabaseProfile = () => (dispatch, getState) => {
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.reject(new Error('No authenticated user'));
  }

  dispatch(fetchSupabaseProfileRequest());

  return fetchSupabaseProfileRequestOnce()
    .then(async res => {
      if (res.status === 401) {
        await delay(350);
        return fetchSupabaseProfileRequestOnce();
      }

      return res;
    })
    .then(res => {
      if (res.status === 404) {
        // Profile doesn't exist, trigger sync
        return dispatch(syncProfileFromSharetribe()).then(profile => ({ profile }));
      }
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to fetch profile');
        });
      }
      return res.json();
    })
    .then(data => {
      if (data && data.profile) {
        dispatch(fetchSupabaseProfileSuccess(data.profile));
        return data.profile;
      }
      throw new Error('Invalid response format');
    })
    .catch(e => {
      log.error(e, 'fetch-supabase-profile-failed');
      dispatch(fetchSupabaseProfileError(storableError(e)));
      throw e;
    });
};

/**
 * Update Supabase profile for the current user
 * @param {Object} profileData - Profile fields to update (handle, bio, avatar_url, etc.)
 */
export const updateSupabaseProfile = profileData => (dispatch, getState) => {
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.reject(new Error('No authenticated user'));
  }

  dispatch(updateSupabaseProfileRequest());

  return fetch(`${apiBaseUrl()}/api/supabase/profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(profileData),
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to update profile');
        });
      }
      return res.json();
    })
    .then(data => {
      if (data && data.profile) {
        dispatch(updateSupabaseProfileSuccess(data.profile));
        return data.profile;
      }
      throw new Error('Invalid response format');
    })
    .catch(e => {
      log.error(e, 'update-supabase-profile-failed');
      dispatch(updateSupabaseProfileError(storableError(e)));
      throw e;
    });
};

/**
 * Sync profile from Sharetribe to Supabase
 * Creates a new Supabase profile record based on Sharetribe user data
 */
export const syncProfileFromSharetribe = () => (dispatch, getState) => {
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.reject(new Error('No authenticated user'));
  }

  dispatch(syncProfileRequest());
  const syncPayload = buildProfileSyncPayload(currentUser);

  return syncSupabaseProfileRequestOnce(syncPayload)
    .then(async res => {
      if (res.status === 401) {
        await delay(350);
        return syncSupabaseProfileRequestOnce(syncPayload);
      }

      return res;
    })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to sync profile');
        });
      }
      return res.json();
    })
    .then(data => {
      if (data && data.profile) {
        dispatch(syncProfileSuccess(data.profile));
        return data.profile;
      }
      throw new Error('Invalid response format');
    })
    .catch(e => {
      log.error(e, 'sync-profile-failed');
      dispatch(syncProfileError(storableError(e)));
      throw e;
    });
};
